import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Plus, UserPlus, X } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * WHO THIS INVOICE GOES TO — a list, not a text box.
 *
 * The composer used to render one `<input type="email">` pre-filled from the
 * client's single `billingEmail` column, and the mailer behind it refused a
 * comma-separated list rather than splitting it. So an owner who wanted AP, the
 * client's agreement manager and a copy for his own records sent from the app and
 * then forwarded the message by hand from Gmail — every invoice, forever, which
 * is exactly the step this domain exists to delete.
 *
 * The design constraint is Jesse's: **fewer steps beats more fidelity.** So
 * this is deliberately NOT a contacts screen with a create form:
 *
 * - it opens already holding everyone the client record knows about;
 * - typing an address and pressing Enter, comma or Tab adds it;
 * - **what you send to is remembered against the client**, so the second
 *   invoice to that client is already addressed to the whole group with no
 *   trip to a settings page;
 * - "Copy me" is one toggle wearing the signed-in user's own address, and it
 *   stays on until it is turned off.
 *
 * Pasting a block out of a mail client works too — the server splits commas,
 * semicolons, newlines and "Name <addr>" and reports anything it could not
 * read rather than dropping it.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

/** Split a typed or pasted blob the same way the server does. */
export function splitAddresses(value: string): string[] {
  return value
    .split(/[,;\n\r\t]+|\s+/)
    .map((part) => {
      const angled = /<([^>]+)>/.exec(part);
      return (angled ? angled[1] : part).trim().replace(/^["']|["']$/g, '');
    })
    .filter(Boolean);
}

/** Case-insensitive union, first-seen order kept. */
export function addAddresses(list: string[], incoming: string[]): string[] {
  const out = [...list];
  const seen = new Set(list.map((v) => v.toLowerCase()));
  for (const value of incoming) {
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out;
}

/**
 * ⚠ **THE RULE WAS ONLY IN THIS FILE'S SOURCE COMMENT.**
 *
 * "The first chip is the addressee and the rest are copied" was true, load-
 * bearing, and stated nowhere a sender could see: the caption said "everyone
 * here gets the invoice and its PDF" and the counter said "ap@… + 3 copied",
 * which between them described the behaviour without ever naming which chip was
 * which. And promoting a different address to To was impossible — you deleted
 * every chip and re-typed them in the order you wanted.
 *
 * So each chip wears its role, and the CC tag is a BUTTON: one click makes that
 * address the addressee and demotes the old one. It costs nothing when the
 * default order is right, which it almost always is.
 */
function Chip({
  address,
  invalid,
  role,
  onRemove,
  onPromote,
}: {
  address: string;
  invalid: boolean;
  role: 'to' | 'cc';
  onRemove: () => void;
  onPromote?: () => void;
}) {
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1 rounded-full py-1 pl-1.5 pr-1 text-caption font-semibold',
        invalid
          ? 'bg-status-late-bg text-status-late ring-1 ring-status-late-border'
          : 'bg-grey-100 text-secondary',
      )}
    >
      {role === 'to' ? (
        <span
          className="shrink-0 rounded-full bg-secondary px-1.5 py-px text-[10px] uppercase tracking-wider text-white"
          title="The addressee — this invoice is written to them"
        >
          to
        </span>
      ) : (
        <button
          type="button"
          onClick={onPromote}
          title={`Make ${address} the addressee — everyone else is copied`}
          aria-label={`Make ${address} the addressee`}
          className="shrink-0 rounded-full bg-grey-200 px-1.5 py-px text-[10px] uppercase tracking-wider text-grey-600 transition-colors hover:bg-secondary hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
        >
          cc
        </button>
      )}
      {invalid && <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />}
      <span className="num truncate">{address}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${address}`}
        title={`Remove ${address}`}
        className="shrink-0 rounded-full p-0.5 text-grey-400 transition-colors hover:bg-white hover:text-secondary"
      >
        <X className="h-3 w-3" aria-hidden />
      </button>
    </span>
  );
}

export interface RecipientsValue {
  to: string[];
  cc: string[];
  copyMe: boolean;
}

/**
 * The To row. One control, because "To" and "CC" are one decision at this size
 * — the first chip is the addressee and the rest are copied, which is what the
 * caption says. A separate CC field would be a second row and a second thought
 * for a job that is "who should see this".
 */
export function RecipientsField({
  value,
  onChange,
  copyToAddress,
  /** Everything the client record already holds, for the "restore" hint. */
  known,
  clientName,
  /** Save the current list onto the client explicitly (removals included). */
  onSaveToClient,
  saving,
  disabled,
}: {
  value: RecipientsValue;
  onChange: (next: RecipientsValue) => void;
  copyToAddress: string | null;
  known: string[];
  clientName: string;
  onSaveToClient?: () => void;
  saving?: boolean;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const all = useMemo(() => [...value.to, ...value.cc], [value.to, value.cc]);
  const invalid = all.filter((v) => !isEmail(v));

  const commit = (raw: string): boolean => {
    const parts = splitAddresses(raw);
    if (parts.length === 0) return false;
    // The first address is the addressee; everything after it is copied. A
    // client with no billing email on file gets its first typed address
    // promoted, rather than an empty To and a full CC.
    const merged = addAddresses(all, parts);
    onChange({
      ...value,
      to: merged.slice(0, 1),
      cc: merged.slice(1),
    });
    setDraft('');
    return true;
  };

  const removeAt = (address: string) => {
    const merged = all.filter((v) => v.toLowerCase() !== address.toLowerCase());
    onChange({ ...value, to: merged.slice(0, 1), cc: merged.slice(1) });
  };

  /** Make one of the copied addresses the addressee. Order is the model. */
  const promote = (address: string) => {
    const rest = all.filter((v) => v.toLowerCase() !== address.toLowerCase());
    onChange({ ...value, to: [address], cc: rest });
  };

  const missing = known.filter((k) => !all.some((v) => v.toLowerCase() === k.toLowerCase()));

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="block text-caption font-bold uppercase tracking-wider text-grey-500">
          To
        </span>
        <span className="text-caption text-grey-500">
          {all.length <= 1
            ? '1 recipient'
            : `${all[0]} + ${all.length - 1} copied`}
        </span>
      </div>
      <div
        onClick={() => inputRef.current?.focus()}
        className={cn(
          'mt-1 flex flex-wrap items-center gap-1.5 rounded-lg border px-2 py-2 transition-colors',
          invalid.length > 0 ? 'border-status-late' : 'border-grey-300',
          'focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/20',
          disabled && 'pointer-events-none opacity-60',
        )}
      >
        {all.map((address, index) => (
          <Chip
            key={address}
            address={address}
            invalid={!isEmail(address)}
            role={index === 0 ? 'to' : 'cc'}
            onRemove={() => removeAt(address)}
            onPromote={() => promote(address)}
          />
        ))}
        <input
          ref={inputRef}
          type="text"
          inputMode="email"
          autoComplete="off"
          spellCheck={false}
          value={draft}
          placeholder={all.length === 0 ? 'ap@client.example' : 'Add another…'}
          aria-label="Add a recipient"
          onChange={(e) => {
            const next = e.target.value;
            // A paste of "a@x.com, b@x.com" becomes two chips immediately —
            // nobody should have to press Enter after a paste.
            if (/[,;\n\t]/.test(next)) {
              commit(next);
              return;
            }
            setDraft(next);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
              if (draft.trim()) {
                e.preventDefault();
                commit(draft);
              }
              return;
            }
            if (e.key === 'Tab' && draft.trim()) {
              commit(draft);
              return;
            }
            if (e.key === 'Backspace' && !draft && all.length > 0) {
              removeAt(all[all.length - 1]);
            }
          }}
          // Leaving the field must not silently discard what was typed: a
          // half-added address that vanishes on blur is how a recipient goes
          // missing without anybody noticing.
          onBlur={() => {
            if (draft.trim()) commit(draft);
          }}
          className="num min-w-[10rem] flex-1 border-0 bg-transparent p-0.5 text-body text-secondary outline-none placeholder:font-sans placeholder:text-grey-400"
        />
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-caption">
        {invalid.length > 0 ? (
          <span className="font-semibold text-status-late">
            {invalid.join(', ')} {invalid.length === 1 ? 'is' : 'are'} not an email address — remove
            it or correct it.
          </span>
        ) : (
          <span className="text-grey-500">
            {all.length > 1 ? 'The first is the addressee, the rest are copied — click a CC to swap. ' : ''}
            Everyone here gets the invoice and its PDF. New addresses are saved to {clientName} for
            next time.
          </span>
        )}
        {missing.length > 0 && (
          <button
            type="button"
            onClick={() => onChange({ ...value, ...splitBack(addAddresses(all, missing)) })}
            className="inline-flex items-center gap-1 font-semibold text-accent-700 hover:underline"
          >
            <Plus className="h-3 w-3" aria-hidden />
            Put back {missing.join(', ')}
          </button>
        )}
        {onSaveToClient && (
          <button
            type="button"
            onClick={onSaveToClient}
            disabled={saving || invalid.length > 0}
            title={`Make this exactly ${clientName}'s billing contact list`}
            className="inline-flex items-center gap-1 font-semibold text-accent-700 hover:underline disabled:opacity-50"
          >
            <UserPlus className="h-3 w-3" aria-hidden />
            {saving ? 'Saving…' : 'Save this list to the client'}
          </button>
        )}
      </div>

      {/* ONE TOGGLE, AND IT STAYS ON. "Send a copy to me" is a filing habit,
          not a per-message decision, so the choice is remembered (see
          `useCopyMe`). It is a BCC — printing our own inbox on every
          client-facing invoice is noise the client did not ask for. */}
      {copyToAddress && (
        <label className="mt-2.5 flex cursor-pointer items-center gap-2 text-body-sm text-secondary">
          <input
            type="checkbox"
            checked={value.copyMe}
            onChange={(e) => onChange({ ...value, copyMe: e.target.checked })}
            className="h-4 w-4 shrink-0 cursor-pointer rounded border-grey-300 accent-accent"
          />
          <span>
            Send a copy to me{' '}
            <span className="num text-grey-500">({copyToAddress}, blind-copied)</span>
          </span>
        </label>
      )}
    </div>
  );
}

function splitBack(list: string[]): { to: string[]; cc: string[] } {
  return { to: list.slice(0, 1), cc: list.slice(1) };
}

const COPY_ME_KEY = 'solid-financials.invoice.copyMe';

/**
 * The "copy me" preference, remembered. A toggle that resets on every message
 * is a click per invoice, which is the kind of step this whole wave removes.
 * It lives in localStorage rather than on the server because it is a habit of
 * the person sending, not a property of the invoice.
 */
export function useCopyMe(): [boolean, (next: boolean) => void] {
  const [on, setOn] = useState(() => {
    try {
      return window.localStorage.getItem(COPY_ME_KEY) === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(COPY_ME_KEY, on ? '1' : '0');
    } catch {
      /* a private window is not a reason to fail a send */
    }
  }, [on]);
  return [on, setOn];
}
